import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, UploadCloud, Trash2, RefreshCw, AlertCircle, CheckCircle2,
  Loader, Search, X,
} from 'lucide-react';
import ragService from '../../api/services/ragService';

const STATUS = {
  uploading: { label: 'Envoi', color: 'bg-blue-500/20 text-blue-400', icon: Loader },
  processing: { label: 'Indexation', color: 'bg-yellow-500/20 text-yellow-500', icon: Loader },
  indexed: { label: 'Indexé', color: 'bg-green-500/20 text-green-500', icon: CheckCircle2 },
  failed: { label: 'Échec', color: 'bg-red-500/20 text-red-500', icon: AlertCircle },
};

const formatSize = (bytes) => {
  if (!bytes) return '0 o';
  const u = ['o', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${u[i]}`;
};

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const fileInput = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await ragService.listDocuments(params);
      if (res.success) setDocuments(res.data || []);
    } catch (err) {
      setError(err.error?.message || err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh tant qu'un document est en cours d'indexation
  useEffect(() => {
    const inProgress = documents.some((d) => d.status === 'processing' || d.status === 'uploading');
    if (inProgress && !pollRef.current) {
      pollRef.current = setInterval(load, 4000);
    } else if (!inProgress && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [documents, load]);

  const handleFiles = async (files) => {
    const list = Array.from(files);
    for (const file of list) {
      try {
        setUploading(true);
        setProgress(0);
        await ragService.uploadDocument(file, {}, setProgress);
      } catch (err) {
        setError(err.error?.message || err.message || `Échec de l'envoi de ${file.name}`);
      }
    }
    setUploading(false);
    setProgress(0);
    load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce document ? Il sera retiré de la base de connaissance.')) return;
    try {
      await ragService.deleteDocument(id);
      setDocuments((docs) => docs.filter((d) => d.id !== id));
    } catch (err) {
      setError(err.error?.message || err.message || 'Erreur de suppression');
    }
  };

  const filtered = documents.filter((d) =>
    search ? d.fileName?.toLowerCase().includes(search.toLowerCase()) : true
  );

  return (
    <div>
      <div className="mb-6">
        <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>RAG</p>
        <h2 className="text-ink font-semibold" style={{ fontSize: '18px' }}>Documents (RAG)</h2>
        <p className="text-label text-sm mt-1">
          Importe tes documents — Gemini les indexe automatiquement pour le chat IA.
        </p>
      </div>

      {/* Zone de drop */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInput.current?.click()}
        className="cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-all mb-6"
        style={dragOver
          ? { borderColor: 'rgb(var(--c-accent-green-rgb))', background: 'rgba(var(--c-accent-green-rgb), 0.08)' }
          : { borderColor: 'var(--c-border)', background: 'var(--c-primary-card)' }
        }
      >
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          accept=".pdf,.docx,.doc,.txt,.csv,.xlsx,.xls"
          onChange={(e) => e.target.files?.length && handleFiles(e.target.files)}
        />
        <UploadCloud className="mx-auto mb-3 text-accent-green" size={40} />
        {uploading ? (
          <div>
            <p className="text-ink font-medium">Envoi en cours… {progress}%</p>
            <div className="mt-2 h-2 w-full max-w-xs mx-auto rounded-full overflow-hidden" style={{ background: 'var(--c-primary-dark)' }}>
              <div className="h-full bg-accent-green transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <>
            <p className="text-ink font-medium">Glisse-dépose tes fichiers ici</p>
            <p className="text-label text-sm mt-1">PDF, DOCX, TXT, CSV, XLSX — 50 Mo max</p>
          </>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertCircle className="text-red-500" size={16} />
          <p className="text-red-400 text-sm flex-1">{error}</p>
          <button onClick={() => setError(null)}><X className="text-label" size={16} /></button>
        </div>
      )}

      {/* Filtres */}
      <div className="card p-4 mb-6 flex flex-col lg:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-label" size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un document…"
            className="w-full text-ink pl-10 pr-4 py-2 rounded-lg outline-none focus:ring-2 focus:ring-accent-green/40"
            style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
          />
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {[
            { id: 'all', label: 'Tous' },
            { id: 'indexed', label: 'Indexés' },
            { id: 'processing', label: 'En cours' },
            { id: 'failed', label: 'Échecs' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                statusFilter === f.id ? 'bg-accent-green text-black' : 'text-label'
              }`}
              style={statusFilter !== f.id ? { background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' } : {}}
            >
              {f.label}
            </button>
          ))}
          <button onClick={load} className="p-2 rounded-lg flex-shrink-0 text-label transition-all hover:bg-[var(--c-hover)]" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent-green" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <FileText className="w-14 h-14 text-label mx-auto mb-3" />
          <p className="text-ink font-semibold">Aucun document</p>
          <p className="text-label text-sm">Importe ton premier document ci-dessus.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((doc) => {
            const st = STATUS[doc.status] || STATUS.processing;
            const Icon = st.icon;
            const spinning = doc.status === 'processing' || doc.status === 'uploading';
            return (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-4 flex items-center gap-4 transition-all hover:border-accent-green/30"
              >
                <div className="w-11 h-11 bg-accent-green/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="text-accent-green" size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-ink font-medium truncate">{doc.fileName}</p>
                  <p className="text-label text-xs">
                    {formatSize(doc.sizeBytes)} • {new Date(doc.createdAt).toLocaleDateString('fr-FR')}
                  </p>
                  {doc.status === 'failed' && doc.errorMessage && (
                    <p className="text-red-400 text-xs mt-1 truncate">{doc.errorMessage}</p>
                  )}
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5 ${st.color}`}>
                  <Icon size={13} className={spinning ? 'animate-spin' : ''} />
                  {st.label}
                </span>
                <button
                  onClick={() => handleDelete(doc.id)}
                  className="p-2 rounded-lg transition-all hover:bg-[var(--c-hover)]"
                >
                  <Trash2 className="text-label hover:text-red-500" size={18} />
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
